import {
  Award,
  BarChart3,
  Bell,
  BookOpen,
  Building,
  Building2,
  Calendar,
  CalendarOff,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CreditCard,
  Database,
  DollarSign,
  FileQuestion,
  GraduationCap,
  HelpCircle,
  Home,
  Inbox,
  LibraryBig,
  Landmark,
  MessageSquare,
  Package,
  PartyPopper,
  QrCode,
  ScrollText,
  Settings,
  ShieldCheck,
  Smartphone,
  Ticket,
  TicketPercent,
  Users,
  UserCog,
  Video,
  type LucideIcon,
} from "lucide-react";
import {
  appendFacilityScopeToUrl,
  requiresSingleFacility,
} from "@/lib/facilityModeAccess";

const FACILITY_ID_PLACEHOLDER = ":facilityId";

export type AdminNavigationLink = {
  kind: "link";
  id: string;
  title: string;
  url: string;
  icon?: LucideIcon;
  keywords?: readonly string[];
  requiresFacility?: boolean;
};

export type AdminNavigationFolder = {
  kind: "folder";
  id: string;
  title: string;
  icon: LucideIcon;
  keywords?: readonly string[];
  items: AdminNavigationLink[];
};

export type AdminNavigationItem = AdminNavigationLink | AdminNavigationFolder;

export type AdminNavigationGroup = {
  id: string;
  label: string;
  icon: LucideIcon;
  items: AdminNavigationItem[];
};

export type AdminQuickAction = {
  id: string;
  title: string;
  description: string;
  url: string;
  icon: LucideIcon;
};

export type AdminNavigationContext = {
  selectedFacilityId: string;
  supportsChildren: boolean;
  isPlatformUser: boolean;
  isPlatformOwner: boolean;
  isOrganizationAdmin: boolean;
  hasGlobalFacilityScope: boolean;
  canManageStaff: boolean;
  canManageRoles: boolean;
  canManageMembers: boolean;
  canManageCourses: boolean;
  canManageInventory: boolean;
  canManageBilling: boolean;
  canExecuteBulkOperations: boolean;
  canPublishNotifications: boolean;
  canManageInquiries: boolean;
  isDirectDebitEnabled: boolean;
  isTicketingEnabled: boolean;
};

type VisibilityRule = (context: AdminNavigationContext) => boolean;

type RawNavigationLink = Omit<AdminNavigationLink, "url"> & {
  url: string;
  /** 組織共通の設定へは、表示中の施設にかかわらず全施設で遷移する。 */
  workspaceFacility?: "all";
  visibleWhen?: VisibilityRule;
};

type RawNavigationFolder = {
  kind: "folder";
  id: string;
  title: string;
  icon: LucideIcon;
  keywords?: readonly string[];
  collapseTitle?: "parent" | "child";
  items: readonly RawNavigationLink[];
};

type RawNavigationItem = RawNavigationLink | RawNavigationFolder;

type RawNavigationGroup = {
  id: string;
  label: string;
  icon: LucideIcon;
  items: readonly RawNavigationItem[];
};

type RawQuickAction = AdminQuickAction & {
  visibleWhen?: VisibilityRule;
};

const link = (
  definition: Omit<RawNavigationLink, "kind">
): RawNavigationLink => ({ kind: "link", ...definition });

const TENANT_NAVIGATION: readonly RawNavigationGroup[] = [
  {
    id: "overview",
    label: "ホーム・分析",
    icon: Home,
    items: [
      link({
        id: "dashboard",
        title: "ダッシュボード",
        url: "/dashboard",
        icon: Home,
        keywords: ["ホーム", "トップ", "今日", "レッスン"],
      }),
      {
        kind: "folder",
        id: "analytics",
        title: "分析",
        icon: BarChart3,
        keywords: ["レポート", "集計", "経営"],
        items: [
          link({
            id: "analytics-management",
            title: "経営ダッシュボード",
            url: "/analytics/management",
            keywords: ["売上", "経営", "KPI"],
          }),
          link({
            id: "analytics-customers",
            title: "集客・顧客分析",
            url: "/analytics/customers",
            keywords: [
              "会員",
              "入会",
              "退会",
              "顧客",
              "イベント集客",
              "参加者",
              "申込",
            ],
          }),
        ],
      },
    ],
  },
  {
    id: "customers-and-requests",
    label: "顧客・申請",
    icon: Contact,
    items: [
      {
        kind: "folder",
        id: "customers",
        title: "顧客管理",
        icon: Contact,
        keywords: ["会員", "顧客"],
        items: [
          link({
            id: "customer-parents",
            title: "会員一覧",
            url: "/customers/members",
            keywords: ["親", "会員", "顧客"],
          }),
          link({
            id: "customer-children",
            title: "受講者一覧",
            url: "/customers/children",
            keywords: [
              "受講者",
              "受講者",
              "成人",
              "大人",
              "受講者",
              "受講者",
              "会員",
            ],
            visibleWhen: (context) => context.supportsChildren,
          }),
          link({
            id: "customer-operation-history",
            title: "操作履歴",
            url: "/customers/operation-history",
            icon: ScrollText,
            keywords: ["履歴", "変更", "差分"],
            visibleWhen: (context) =>
              context.isOrganizationAdmin &&
              context.hasGlobalFacilityScope &&
              context.canManageMembers,
          }),
        ],
      },
      {
        kind: "folder",
        id: "requests",
        title: "各種申請",
        icon: ClipboardList,
        keywords: ["申し込み", "申込み", "受付"],
        items: [
          link({
            id: "trial-applications",
            title: "体験申し込み",
            url: "/trial-applications",
            keywords: ["体験", "見学", "申込"],
          }),
          link({
            id: "admission-requests",
            title: "入会申請管理",
            url: "/admission/requests",
            keywords: ["入会", "新規会員"],
            visibleWhen: (context) => context.canManageMembers,
          }),
          link({
            id: "course-enrollment-requests",
            title: "コース追加申請",
            url: "/course-enrollment-requests",
            keywords: ["コース", "追加", "受講"],
          }),
          link({
            id: "membership-requests",
            title: "休退会申請",
            url: "/membership-requests",
            keywords: ["休会", "退会", "休退会"],
            visibleWhen: (context) => context.canManageBilling,
          }),
        ],
      },
    ],
  },
  {
    id: "daily-operations",
    label: "日々の運営",
    icon: Calendar,
    items: [
      link({
        id: "grade-tests",
        title: "進級管理",
        url: "/grade-tests",
        icon: ClipboardCheck,
        requiresFacility: true,
        keywords: ["進級", "評価", "テスト", "合格", "認定"],
      }),
      {
        kind: "folder",
        id: "attendance-operations",
        title: "振替・出欠",
        icon: Calendar,
        keywords: ["振り替え", "欠席", "遅刻", "休み"],
        items: [
          link({
            id: "reschedule-requests",
            title: "振替管理",
            url: "/reschedule-requests",
            keywords: ["振替", "振り替え", "振替変更", "別日"],
          }),
          link({
            id: "attendance-reports",
            title: "欠席・遅刻報告",
            url: "/attendance-reports",
            keywords: ["欠席", "遅刻", "出欠", "休み"],
          }),
          link({
            id: "ticket-reception",
            title: "チケット受付",
            url: `/facilities/${FACILITY_ID_PLACEHOLDER}/ticket-reservations`,
            requiresFacility: true,
            keywords: ["チケット", "予約", "来場", "出欠"],
          }),
        ],
      },
      link({
        id: "events",
        title: "イベント管理",
        url: "/events",
        icon: PartyPopper,
        keywords: ["催し", "行事", "参加者", "申込"],
      }),
    ],
  },
  {
    id: "classes-and-courses",
    label: "教室・コース",
    icon: BookOpen,
    items: [
      link({
        id: "rooms",
        title: "教室管理",
        url: `/facilities/${FACILITY_ID_PLACEHOLDER}/rooms`,
        icon: Building,
        requiresFacility: true,
        keywords: ["部屋", "会場", "スタジオ"],
      }),
      link({
        id: "courses",
        title: "コース管理",
        url: "/courses",
        icon: BookOpen,
        keywords: ["クラス", "講座", "時間割"],
      }),
      link({
        id: "coaches",
        title: "コーチ管理",
        url: "/coaches",
        icon: GraduationCap,
        keywords: ["先生", "講師", "インストラクター"],
        visibleWhen: (context) =>
          context.isOrganizationAdmin && context.hasGlobalFacilityScope,
      }),
      link({
        id: "grade-programs",
        title: "進級制度管理",
        url: "/grade-programs",
        icon: Award,
        keywords: ["進級", "制度", "級", "グレード", "テスト", "種目"],
      }),
      link({
        id: "learning-catalog",
        title: "マイコース構成",
        url: "/learning-catalog",
        icon: LibraryBig,
        keywords: ["学習教材", "進級制度", "保護者アプリ", "コース"],
        visibleWhen: (context) =>
          context.canManageCourses && context.hasGlobalFacilityScope,
      }),
      link({
        id: "video-materials",
        title: "教材コンテンツ",
        url: "/video-materials",
        icon: Video,
        keywords: ["動画", "教材", "配信", "レッスン"],
        visibleWhen: (context) =>
          context.canManageCourses && context.hasGlobalFacilityScope,
      }),
    ],
  },
  {
    id: "communication",
    label: "コミュニケーション",
    icon: MessageSquare,
    items: [
      {
        kind: "folder",
        id: "notifications",
        title: "お知らせ管理",
        icon: Bell,
        collapseTitle: "parent",
        keywords: ["通知", "一斉配信", "メール"],
        items: [
          link({
            id: "notifications-list",
            title: "お知らせ一覧",
            url: "/notifications",
            keywords: ["通知", "配信", "連絡"],
            visibleWhen: (context) => context.canPublishNotifications,
          }),
          link({
            id: "notification-segments",
            title: "配信グループ",
            url: "/notifications/segments",
            keywords: ["セグメント", "宛先", "一斉配信"],
            visibleWhen: (context) =>
              context.canPublishNotifications && context.hasGlobalFacilityScope,
          }),
        ],
      },
      link({
        id: "chat",
        title: "チャット",
        url: "/chat",
        icon: MessageSquare,
        keywords: ["メッセージ", "連絡", "会話"],
      }),
      link({
        id: "faqs",
        title: "FAQ管理",
        url: "/faqs",
        icon: HelpCircle,
        keywords: ["FAQ", "ヘルプ", "質問"],
      }),
      link({
        id: "surveys",
        title: "アンケート",
        url: "/surveys",
        icon: FileQuestion,
        keywords: ["回答", "調査", "フォーム"],
      }),
      link({
        id: "inquiries",
        title: "お問い合わせ",
        url: "/inquiries",
        icon: Inbox,
        keywords: ["問い合わせ", "相談", "受信"],
        visibleWhen: (context) => context.canManageInquiries,
      }),
    ],
  },
  {
    id: "billing-and-sales",
    label: "販売・請求",
    icon: DollarSign,
    items: [
      {
        kind: "folder",
        id: "billing",
        title: "請求・決済",
        icon: DollarSign,
        keywords: ["月謝", "料金", "支払い", "入金"],
        items: [
          link({
            id: "billing-pricing",
            title: "料金設定",
            url: "/billing/pricing",
            keywords: ["月謝", "料金", "価格"],
            visibleWhen: (context) => context.canManageBilling,
          }),
          link({
            id: "billing-discount-rules",
            title: "月謝割引",
            url: "/billing/discount-rules",
            keywords: ["月謝", "兄弟割引", "複数コース", "顧客個別", "値引き"],
            visibleWhen: (context) => context.canManageBilling,
          }),
          link({
            id: "billing-coupons",
            title: "クーポン",
            url: "/billing/coupons",
            icon: TicketPercent,
            keywords: ["クーポン", "キャンペーン", "特典", "コード", "値引き"],
            visibleWhen: (context) =>
              context.canManageBilling && context.hasGlobalFacilityScope,
          }),
          link({
            id: "billing-ticket-products",
            title: "チケット商品",
            url: "/billing/ticket-products",
            icon: Ticket,
            keywords: ["回数券", "チケット", "有効期限", "都度利用"],
            visibleWhen: (context) =>
              context.canManageBilling &&
              context.hasGlobalFacilityScope &&
              context.isTicketingEnabled,
          }),
          link({
            id: "billing-history",
            title: "請求一覧",
            url: "/billing/history",
            keywords: [
              "月謝",
              "請求",
              "履歴",
              "未収",
              "口座振替",
              "振替予定",
              "引き落とし",
            ],
            visibleWhen: (context) => context.canManageBilling,
          }),
        ],
      },
      {
        kind: "folder",
        id: "products",
        title: "物販管理",
        icon: Package,
        keywords: ["ショップ", "販売"],
        items: [
          link({
            id: "products-list",
            title: "商品一覧",
            url: "/products",
            keywords: ["物販", "商品"],
            visibleWhen: (context) =>
              context.canManageInventory && context.hasGlobalFacilityScope,
          }),
          link({
            id: "product-inventory",
            title: "在庫管理",
            url: "/products/inventory",
            keywords: ["在庫", "数量", "入荷"],
          }),
          link({
            id: "orders",
            title: "注文一覧",
            url: "/orders",
            keywords: ["注文", "購入", "受注"],
          }),
        ],
      },
    ],
  },
  {
    id: "settings-and-access",
    label: "管理設定",
    icon: Settings,
    items: [
      {
        kind: "folder",
        id: "organization-and-facilities",
        title: "組織・施設",
        icon: Building2,
        keywords: ["基本情報", "拠点", "ブランド"],
        items: [
          link({
            id: "organization-branding",
            title: "組織情報",
            url: "/organization/branding",
            keywords: ["ロゴ", "ブランド", "組織名"],
            visibleWhen: (context) => context.isOrganizationAdmin,
          }),
          link({
            id: "organization-payout-account",
            title: "振込先口座",
            url: "/organization/payout-account",
            workspaceFacility: "all",
            icon: Landmark,
            keywords: ["銀行", "振込", "売上", "口座", "精算"],
            visibleWhen: (context) =>
              context.canManageBilling && context.hasGlobalFacilityScope,
          }),
          link({
            id: "facilities",
            title: "施設管理",
            url: "/facilities",
            keywords: ["拠点", "校舎", "施設"],
          }),
          link({
            id: "settings-data-import",
            title: "データ移行",
            url: "/settings/data-import",
            icon: Database,
            keywords: ["CSV", "インポート", "移行", "hacomono"],
            visibleWhen: (context) =>
              context.hasGlobalFacilityScope &&
              context.canExecuteBulkOperations,
          }),
        ],
      },
      {
        kind: "folder",
        id: "operation-rules",
        title: "運用ルール",
        icon: Settings,
        keywords: ["ポリシー", "設定"],
        items: [
          link({
            id: "organization-member-management-mode",
            title: "会員の利用形態",
            url: "/organization/member-management-mode",
            icon: Users,
            keywords: ["本人", "受講者", "会員管理", "運用モード"],
            visibleWhen: (context) =>
              context.isOrganizationAdmin && context.hasGlobalFacilityScope,
          }),
          link({
            id: "organization-reschedule-policy",
            title: "振替ルール",
            url: "/organization/reschedule-policy",
            icon: Calendar,
            keywords: ["振替", "振り替え", "期限"],
            visibleWhen: (context) =>
              context.canManageRoles && context.hasGlobalFacilityScope,
          }),
          link({
            id: "organization-membership-policy",
            title: "会員変更ルール",
            url: "/organization/membership-policy",
            icon: CalendarOff,
            keywords: ["プラン変更", "休会", "復会", "退会", "締切"],
            visibleWhen: (context) =>
              context.canManageRoles && context.hasGlobalFacilityScope,
          }),
          link({
            id: "organization-attendance-setting",
            title: "出欠設定",
            url: "/organization/attendance-setting",
            icon: ClipboardCheck,
            keywords: ["出席", "欠席", "遅刻"],
            visibleWhen: (context) =>
              context.canManageRoles && context.hasGlobalFacilityScope,
          }),
          link({
            id: "organization-chat-support-policy",
            title: "チャット受付設定",
            url: "/organization/chat-support-policy",
            icon: MessageSquare,
            keywords: ["チャット", "受付時間", "メッセージ"],
            visibleWhen: (context) =>
              context.canManageRoles && context.hasGlobalFacilityScope,
          }),
          link({
            id: "settings-ticketing",
            title: "チケット運用",
            url: "/settings/ticketing",
            icon: Ticket,
            keywords: ["回数券", "チケット", "利用設定", "機能設定"],
            visibleWhen: (context) =>
              context.canManageBilling && context.hasGlobalFacilityScope,
          }),
        ],
      },
      {
        kind: "folder",
        id: "application-settings",
        title: "受付・規約",
        icon: QrCode,
        keywords: ["入会", "体験", "フォーム", "規約"],
        items: [
          link({
            id: "trial-form-templates",
            title: "フォームテンプレート設定",
            url: "/trial-form-templates",
            keywords: ["体験", "フォーム", "テンプレート"],
            visibleWhen: (context) => context.canManageCourses,
          }),
          link({
            id: "trial-settings",
            title: "体験受付設定",
            url: "/trial-settings",
            keywords: ["体験", "受付", "申込"],
            visibleWhen: (context) => context.canManageCourses,
          }),
          link({
            id: "admission-code",
            title: "入会コード",
            url: "/admission/code",
            icon: QrCode,
            keywords: ["QR", "入会", "コード"],
            visibleWhen: (context) => context.canManageMembers,
          }),
          link({
            id: "admission-terms",
            title: "入会利用規約",
            url: "/admission/terms",
            icon: ScrollText,
            keywords: ["入会", "規約", "同意"],
            visibleWhen: (context) =>
              context.canManageRoles && context.hasGlobalFacilityScope,
          }),
        ],
      },
      {
        kind: "folder",
        id: "staff-and-notifications",
        title: "スタッフ・通知",
        icon: Users,
        keywords: ["職員", "権限", "メール"],
        items: [
          link({
            id: "staff",
            title: "スタッフ管理",
            url: "/staff",
            keywords: ["職員", "従業員", "権限", "招待"],
            visibleWhen: (context) => context.canManageStaff,
          }),
          link({
            id: "staff-notification-settings",
            title: "通知・メール設定",
            url: "/staff/notification-settings",
            keywords: ["通知", "メール", "配信設定"],
            visibleWhen: (context) =>
              context.canManageRoles && context.canPublishNotifications,
          }),
        ],
      },
      link({
        id: "billing-payment-setup",
        title: "決済サービス設定",
        url: "/billing/payment-setup",
        workspaceFacility: "all",
        icon: CreditCard,
        keywords: ["決済", "カード", "口座振替", "申込"],
        visibleWhen: (context) =>
          context.canManageBilling && context.hasGlobalFacilityScope,
      }),
    ],
  },
];

const PLATFORM_NAVIGATION: readonly RawNavigationGroup[] = [
  {
    id: "platform-operations",
    label: "スマコミ運営",
    icon: ShieldCheck,
    items: [
      link({
        id: "operator-tenant-payments",
        title: "テナント決済審査",
        url: "/operator/tenant-payments",
        icon: ShieldCheck,
        keywords: ["審査", "決済", "テナント"],
      }),
      link({
        id: "billing-direct-debit",
        title: "口座振替（運営）",
        url: "/operator/direct-debit",
        icon: Landmark,
        workspaceFacility: "all",
        keywords: [
          "口振",
          "銀行",
          "引落",
          "引き落とし",
          "全銀",
          "年間スケジュール",
        ],
        visibleWhen: (context) =>
          context.isDirectDebitEnabled &&
          context.canManageBilling &&
          context.canExecuteBulkOperations &&
          context.hasGlobalFacilityScope,
      }),
      link({
        id: "operator-platform-accesses",
        title: "運営権限",
        url: "/operator/platform-accesses",
        icon: UserCog,
        keywords: ["運営", "権限", "アカウント"],
        visibleWhen: (context) => context.isPlatformOwner,
      }),
      link({
        id: "operator-data-reconciliation",
        title: "データ整理",
        url: "/operator/data-reconciliation",
        icon: Database,
        keywords: ["データ", "統合", "重複"],
        visibleWhen: (context) => context.isPlatformOwner,
      }),
      link({
        id: "operator-data-migrations",
        title: "データ移行",
        url: "/operator/data-migrations",
        icon: Database,
        keywords: ["移行", "CSV", "hacomono", "履歴"],
        visibleWhen: (context) => context.isPlatformOwner,
      }),
      link({
        id: "operator-app-status",
        title: "アプリ配信管理",
        url: "/operator/app-status",
        icon: Smartphone,
        keywords: ["アプリ", "バージョン", "配信"],
        visibleWhen: (context) => context.isPlatformOwner,
      }),
    ],
  },
];

const TENANT_QUICK_ACTIONS: readonly RawQuickAction[] = [
  {
    id: "quick-parent-invite",
    title: "会員を追加",
    description: "会員を登録し、必要に応じて利用開始メールを送る",
    url: "/customers/members?add=1",
    icon: Contact,
    visibleWhen: (context) => context.canManageMembers,
  },
  {
    id: "quick-child-create",
    title: "受講者を登録",
    description: "新しい受講者情報を登録する",
    url: "/customers/children/create",
    icon: Users,
    visibleWhen: (context) =>
      context.canManageMembers && context.supportsChildren,
  },
  {
    id: "quick-course-create",
    title: "コースを追加",
    description: "対象施設に新しいコースを作る",
    url: "/courses/create",
    icon: BookOpen,
    visibleWhen: (context) => context.canManageCourses,
  },
  {
    id: "quick-event-create",
    title: "イベントを作成",
    description: "募集イベントを新しく作る",
    url: "/events/create",
    icon: PartyPopper,
  },
  {
    id: "quick-notification-create",
    title: "お知らせを作成",
    description: "会員向けのお知らせを配信する",
    url: "/notifications/create",
    icon: Bell,
    visibleWhen: (context) => context.canPublishNotifications,
  },
];

function resolveUrl(url: string, selectedFacilityId: string): string {
  const safeFacilitySegment = encodeURIComponent(
    selectedFacilityId.trim() || "all"
  );
  const resolvedPath = url.replace(
    FACILITY_ID_PLACEHOLDER,
    safeFacilitySegment
  );
  return appendFacilityScopeToUrl(
    resolvedPath,
    selectedFacilityId.trim() || "all"
  );
}

export function getAdminNavigationPathname(url: string): string {
  return url.split(/[?#]/, 1)[0] || "/";
}

/**
 * 現在地に対応するサイドメニュー項目かを判定する。
 * 通知一覧は右上ベルから開くスタッフ個人向け画面であり、
 * 配信管理の「お知らせ一覧」を選択状態にしない。
 */
export function isAdminNavigationPathActive(
  pathname: string,
  url: string
): boolean {
  const destinationPathname = getAdminNavigationPathname(url);
  if (
    pathname === "/notifications/inbox" &&
    destinationPathname === "/notifications"
  ) {
    return false;
  }

  return (
    pathname === destinationPathname ||
    pathname.startsWith(`${destinationPathname}/`)
  );
}

function resolveLink(
  definition: RawNavigationLink,
  context: AdminNavigationContext,
  inheritedIcon?: LucideIcon,
  inheritedKeywords: readonly string[] = []
): AdminNavigationLink | null {
  if (definition.visibleWhen && !definition.visibleWhen(context)) return null;

  const url = resolveUrl(
    definition.url,
    definition.workspaceFacility ?? context.selectedFacilityId
  );

  return {
    kind: "link",
    id: definition.id,
    title: definition.title,
    url,
    icon: definition.icon ?? inheritedIcon,
    keywords: [...inheritedKeywords, ...(definition.keywords ?? [])],
    // 実際にGuardが利用するルート能力から導出し、メニュー定義との二重管理を避ける。
    requiresFacility: requiresSingleFacility(getAdminNavigationPathname(url)),
  };
}

function resolveItem(
  definition: RawNavigationItem,
  context: AdminNavigationContext
): AdminNavigationItem | null {
  if (definition.kind === "link") return resolveLink(definition, context);

  const items = definition.items
    .map((item) =>
      resolveLink(item, context, undefined, [
        definition.title,
        ...(definition.keywords ?? []),
      ])
    )
    .filter((item): item is AdminNavigationLink => item !== null);

  if (items.length === 0) return null;
  if (items.length === 1) {
    const onlyItem = items[0];
    return {
      ...onlyItem,
      title:
        definition.collapseTitle === "parent"
          ? definition.title
          : onlyItem.title,
      icon: definition.icon,
      keywords: [
        definition.title,
        ...(definition.keywords ?? []),
        onlyItem.title,
        ...(onlyItem.keywords ?? []),
      ],
    };
  }

  return {
    kind: "folder",
    id: definition.id,
    title: definition.title,
    icon: definition.icon,
    keywords: definition.keywords,
    items,
  };
}

export function resolveAdminNavigation(
  context: AdminNavigationContext
): AdminNavigationGroup[] {
  const definitions = context.isPlatformUser
    ? PLATFORM_NAVIGATION
    : TENANT_NAVIGATION;

  return definitions
    .map((group) => ({
      id: group.id,
      label: group.label,
      icon: group.icon,
      items: group.items
        .map((item) => resolveItem(item, context))
        .filter((item): item is AdminNavigationItem => item !== null),
    }))
    .filter((group) => group.items.length > 0);
}

export function resolveAdminQuickActions(
  context: AdminNavigationContext
): AdminQuickAction[] {
  if (context.isPlatformUser) return [];

  return TENANT_QUICK_ACTIONS.filter(
    (action) => !action.visibleWhen || action.visibleWhen(context)
  ).map(({ visibleWhen: _visibleWhen, ...action }) => ({
    ...action,
    url: appendFacilityScopeToUrl(
      action.url,
      context.selectedFacilityId.trim() || "all"
    ),
  }));
}

export function isNavigationLinkDisabled(
  _link: AdminNavigationLink,
  _isAllFacilities: boolean
): boolean {
  // 組織全体でも遷移自体は可能にし、単一施設が必要な場合は
  // FacilityModeGuard で施設を選んでから同じ遷移先へ続行する。
  return false;
}

export type AdminNavigationDestination = AdminNavigationLink & {
  groupId: string;
  groupLabel: string;
  parentId?: string;
  parentTitle?: string;
};

export function flattenAdminNavigation(
  groups: readonly AdminNavigationGroup[]
): AdminNavigationDestination[] {
  return groups.flatMap((group) =>
    group.items.flatMap((item) => {
      if (item.kind === "link") {
        return [{ ...item, groupId: group.id, groupLabel: group.label }];
      }

      return item.items.map((link) => ({
        ...link,
        icon: item.icon,
        keywords: [
          item.title,
          ...(item.keywords ?? []),
          ...(link.keywords ?? []),
        ],
        groupId: group.id,
        groupLabel: group.label,
        parentId: item.id,
        parentTitle: item.title,
      }));
    })
  );
}
